import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Building,
  Shield,
  UserPlus,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Crown,
  Plus,
  Loader2,
  AlertCircle,
  Key,
  Mail,
  Download,
  RefreshCw,
  CheckCheck,
  Trash2,
  Edit2,
  Save,
  X,
  UserMinus,
  Users,
  Search,
} from "lucide-react";
import { useUser } from "../contextapi/UserContext";
import {
  getUserOrganizations,
  getOrganizationMembers,
  updateOrganization,
  deleteOrganization,
  removeOrganizationMember,
  updateMemberRole,
} from "../services/organizationService";
import { getOrganizationGroups, Team } from "../services/teamService";
import { useToast } from "../hooks/use-toast";
import { CreateOrganizationDialog } from "../components/organization/CreateOrganizationDialog";
import { PhoneInput } from "../components/ui/phone-input";
import { PhoneValidationResult } from "../utils/phoneValidation";
import { usePermissions } from "../contextapi/PermissionsContext";
import { SettingsPermissionsTab } from "../../../../features/scopingAi/components/SettingsPermissionsTab";
import { emailApiService, EmailAccount } from "../../../../features/scopingAi/services/email/emailApiService";
import { InvitationsSettings } from "../components/settings/InvitationsSettings";
import { OrganizationMember } from "../types/organization";

type SettingsTab =
  | "profile"
  | "organizations"
  | "branding"
  | "subscription"
  | "preferences"
  | "security"
  | "permissions"
  | "invitations";

interface Organization {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  role: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  }
  return (email?.[0] ?? "U").toUpperCase();
}

const CARD = "bg-white dark:bg-card border border-border/60 rounded-2xl shadow-sm";
const BTN_PRIMARY =
  "inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_OUTLINE =
  "inline-flex items-center gap-2 px-4 py-2 border border-border text-sm font-medium rounded-lg hover:bg-muted/60 transition-colors disabled:opacity-50";
const INPUT =
  "w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-background";

// ─── Main Settings Shell ──────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { user } = useUser();
  const { isAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [managingOrg, setManagingOrg] = useState<Organization | null>(null);

  const NAV_ITEMS = [
    { id: "profile" as SettingsTab,       label: "Profile",       icon: User,      sub: "Personal info & security"      },
    { id: "organizations" as SettingsTab, label: "Organisation",  icon: Building,  sub: "Manage your organisation"      },
    ...(isAdmin ? [
      { id: "permissions" as SettingsTab, label: "Permissions",   icon: Shield,    sub: "Manage role-based permissions" },
      { id: "invitations" as SettingsTab, label: "Invitations",   icon: UserPlus,  sub: "Invite members to your org"    },
    ] : []),
  ];

  const initials = getInitials(user?.user_metadata?.full_name, user?.email);

  const handleTabChange = (tab: SettingsTab) => {
    setManagingOrg(null);
    setActiveTab(tab);
  };

  const renderContent = () => {
    if (activeTab === "organizations" && managingOrg) {
      return (
        <OrgManageView
          org={managingOrg}
          onDeleted={() => setManagingOrg(null)}
        />
      );
    }
    switch (activeTab) {
      case "profile":       return <ProfileSettings />;
      case "organizations": return <OrganizationsSettings onManage={setManagingOrg} />;
      case "permissions":   return <SettingsPermissionsTab />;
      case "invitations":   return <InvitationsSettings />;
      default:              return <ProfileSettings />;
    }
  };

  const currentNav = NAV_ITEMS.find((n) => n.id === activeTab);

  return (
    <div className="flex h-full bg-gray-50 dark:bg-background overflow-hidden">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="w-72 flex-shrink-0 bg-white dark:bg-card border-r border-border flex flex-col h-full">
        {/* User card */}
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-base flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate leading-tight">
                {user?.user_metadata?.full_name || "User"}
              </p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <p className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider px-3 pt-2 pb-1">
            Settings
          </p>
          {NAV_ITEMS.map(({ id, label, icon: Icon, sub }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150 ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                <Icon
                  className={`flex-shrink-0 ${active ? "text-primary" : ""}`}
                  style={{ width: 18, height: 18 }}
                />
                <div className="min-w-0">
                  <p className={`text-sm font-medium leading-tight ${active ? "text-primary" : ""}`}>
                    {label}
                  </p>
                  <p className="text-xs text-muted-foreground/70 truncate leading-tight mt-0.5">
                    {sub}
                  </p>
                </div>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar / breadcrumb */}
        <div className="bg-white dark:bg-card border-b border-border px-8 py-3.5 flex-shrink-0 flex items-center gap-2 text-sm">
          {managingOrg ? (
            <>
              <button
                onClick={() => setManagingOrg(null)}
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Organisation
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
              <span className="font-semibold text-foreground">{managingOrg.name}</span>
            </>
          ) : (
            <span className="font-semibold text-foreground">{currentNav?.label ?? "Settings"}</span>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-full px-8 py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab + (managingOrg?.id ?? "")}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.18 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Inline Org Manage View ───────────────────────────────────────────────────

const PAGE_SIZE = 8;

const OrgManageView: React.FC<{
  org: Organization;
  onDeleted: () => void;
}> = ({ org, onDeleted }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [orgRoles, setOrgRoles] = useState<string[]>([]);
  const [orgForm, setOrgForm] = useState({
    name: org.name,
    description: org.description ?? "",
  });

  const isAdmin = org.role === "admin";

  const loadData = useCallback(async () => {
    try {
      setLoadingData(true);
      const [membersData, teamsData] = await Promise.all([
        getOrganizationMembers(org.id),
        getOrganizationGroups(org.id),
      ]);
      setMembers(membersData);
      setTeams(teamsData);
    } catch {
      toast({ title: "Error", description: "Failed to load organisation data.", variant: "destructive" });
    } finally {
      setLoadingData(false);
    }
  }, [org.id]);

  const loadOrgRoles = useCallback(async () => {
    if (!isAdmin || !user?.id) return;
    try {
      const { supabase } = await import("../utils/supabaseClient");
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/scopingai/permissions/roles?orgId=${org.id}`, {
        headers: {
          "x-user-id": user.id,
          "x-org-id": org.id,
          "x-api-key": (import.meta as any).env?.VITE_API_KEY || "scopingai",
          Authorization: session?.access_token ? `Bearer ${session.access_token}` : "",
        },
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setOrgRoles(json.data);
      }
    } catch {
      // silently ignore — fallback to defaults
    }
  }, [org.id, isAdmin, user?.id]);

  useEffect(() => { loadData(); loadOrgRoles(); }, [loadData, loadOrgRoles]);

  const handleSave = async () => {
    if (!orgForm.name.trim()) return;
    try {
      setSaving(true);
      await updateOrganization(org.id, orgForm.name.trim(), orgForm.description);
      toast({ title: "Saved", description: "Organisation updated successfully." });
      setEditMode(false);
    } catch {
      toast({ title: "Error", description: "Failed to update organisation.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (member: OrganizationMember) => {
    try {
      await removeOrganizationMember(org.id, member.user_id);
      toast({ title: "Member removed" });
      loadData();
    } catch {
      toast({ title: "Error", description: "Failed to remove member.", variant: "destructive" });
    }
  };

  const handleRoleChange = async (member: OrganizationMember, newRole: string) => {
    try {
      setUpdatingRole(member.user_id);
      await updateMemberRole(org.id, member.user_id, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.user_id === member.user_id ? { ...m, role: newRole as OrganizationMember["role"] } : m))
      );
      toast({ title: "Role updated", description: `${member.full_name || member.email} is now ${newRole}.` });
    } catch {
      toast({ title: "Error", description: "Failed to update role.", variant: "destructive" });
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDeleteOrg = async () => {
    try {
      setDeleting(true);
      await deleteOrganization(org.id);
      toast({ title: "Organisation deleted" });
      onDeleted();
    } catch {
      toast({ title: "Error", description: "Failed to delete organisation.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    const q = search.toLowerCase();
    return (
      (m.full_name?.toLowerCase().includes(q) ?? false) ||
      (m.email?.toLowerCase().includes(q) ?? false) ||
      m.role.toLowerCase().includes(q)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const pagedMembers = filteredMembers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-5">
      {/* Org Info */}
      <div className={`${CARD} p-6`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold leading-tight">{org.name}</h2>
              <span className={`inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                isAdmin ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              } capitalize`}>
                {isAdmin && <Crown className="w-3 h-3" />}
                {org.role}
              </span>
            </div>
          </div>
          {isAdmin && !editMode && (
            <button onClick={() => setEditMode(true)} className={BTN_OUTLINE}>
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          )}
          {editMode && (
            <button onClick={() => setEditMode(false)} className={BTN_OUTLINE}>
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Organisation Name</label>
              <input
                type="text"
                value={orgForm.name}
                onChange={(e) => setOrgForm((p) => ({ ...p, name: e.target.value }))}
                className={INPUT}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Description</label>
              <textarea
                value={orgForm.description}
                onChange={(e) => setOrgForm((p) => ({ ...p, description: e.target.value }))}
                rows={3}
                className={`${INPUT} resize-none`}
                placeholder="Optional description…"
              />
            </div>
            <div className="flex justify-end">
              <button onClick={handleSave} disabled={saving || !orgForm.name.trim()} className={BTN_PRIMARY}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Created</p>
              <p className="font-medium">
                {new Date(org.created_at).toLocaleDateString("en-AU", {
                  year: "numeric", month: "long", day: "numeric",
                })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Members</p>
              <p className="font-medium">{members.length}</p>
            </div>
            {org.description && (
              <div className="col-span-2">
                <p className="text-muted-foreground text-xs mb-0.5">Description</p>
                <p>{org.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Members */}
      <div className={`${CARD} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Members</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {filteredMembers.length} of {members.length} member{members.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name, email or role…"
            className={`${INPUT} pl-9`}
          />
          {search && (
            <button
              onClick={() => handleSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {loadingData ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading members…</span>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{search ? "No members match your search." : "No members found."}</p>
          </div>
        ) : (
          <>
            <div className="space-y-1 mb-4">
              {pagedMembers.map((member) => {
                const isCurrentUser = member.user_id === user?.id;
                const initials = getInitials(member.full_name, member.email);
                const isUpdating = updatingRole === member.user_id;
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-tight">
                        {member.full_name || "Unknown User"}
                        {isCurrentUser && (
                          <span className="ml-1.5 text-xs text-muted-foreground font-normal">(you)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    </div>
                    {/* Role — dropdown for admin on others, badge for self/non-admin */}
                    {isAdmin && !isCurrentUser ? (
                      <div className="relative flex-shrink-0">
                        {isUpdating ? (
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member, e.target.value)}
                            className="text-xs border border-border rounded-lg px-2 py-1 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                          >
                            {["admin", "member", ...orgRoles]
                              .filter((r, i, arr) => arr.indexOf(r) === i)
                              .map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                          </select>
                        )}
                      </div>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        member.role === "admin"
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}>
                        {member.role}
                      </span>
                    )}
                    {isAdmin && !isCurrentUser && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="p-1.5 rounded-lg hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors flex-shrink-0"
                        title="Remove member"
                        disabled={isUpdating}
                      >
                        <UserMinus className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-border hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors ${
                        page === currentPage
                          ? "bg-primary text-primary-foreground"
                          : "border border-border hover:bg-muted/50 text-muted-foreground"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-border hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Teams */}
      {teams.length > 0 && (
        <div className={`${CARD} p-6`}>
          <div className="mb-5">
            <h3 className="font-semibold">Teams</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {teams.length} team{teams.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="space-y-2">
            {teams.map((team) => (
              <div key={team.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/30 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-blue-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{team.name}</p>
                  {team.description && (
                    <p className="text-xs text-muted-foreground truncate">{team.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Danger Zone */}
      {isAdmin && (
        <div className={`${CARD} border-destructive/20 p-6`}>
          <h3 className="font-semibold text-destructive mb-1">Danger Zone</h3>
          <p className="text-xs text-muted-foreground mb-5">These actions are permanent and cannot be undone.</p>

          {!confirmDelete ? (
            <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-xl">
              <div>
                <p className="text-sm font-medium">Delete Organisation</p>
                <p className="text-xs text-muted-foreground mt-0.5">Permanently delete this organisation and all its data.</p>
              </div>
              <button
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 rounded-lg hover:bg-destructive/10 transition-colors"
              >
                Delete
              </button>
            </div>
          ) : (
            <div className="p-4 border border-destructive/30 rounded-xl bg-destructive/5">
              <p className="text-sm font-semibold text-destructive mb-1">Confirm deletion of "{org.name}"</p>
              <p className="text-xs text-muted-foreground mb-4">
                This will permanently delete the organisation and remove all members. Cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteOrg}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 bg-destructive text-white text-sm rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50"
                >
                  {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Yes, delete
                </button>
                <button onClick={() => setConfirmDelete(false)} className={BTN_OUTLINE}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Organisations Section ────────────────────────────────────────────────────

const OrganizationsSettings: React.FC<{
  onManage: (org: Organization) => void;
}> = ({ onManage }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const orgData = await getUserOrganizations();
      setOrganizations(
        orgData.organizations.map((org) => {
          const membership = orgData.membership.find((m) => m.organisation_id === org.id);
          return {
            id: org.id,
            name: org.name,
            description: org.description,
            created_at: org.created_at,
            created_by: org.created_by,
            updated_at: org.updated_at,
            role: membership?.role || "member",
          };
        })
      );
    } catch {
      toast({ title: "Error", description: "Failed to load organisations.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.id) loadData(); }, [user?.id]);

  if (loading) {
    return (
      <div className={`${CARD} p-10 flex items-center justify-center gap-2`}>
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    );
  }

  return (
    <div className={`${CARD} p-6`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold">Your Organisation</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {organizations.length} organisation{organizations.length !== 1 ? "s" : ""}
          </p>
        </div>
        {organizations.length === 0 && (
          <CreateOrganizationDialog
            onSuccess={loadData}
            trigger={
              <button className={BTN_PRIMARY}>
                <Plus className="w-4 h-4" /> Create
              </button>
            }
          />
        )}
      </div>

      {organizations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Building className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No organisation yet</p>
          <p className="text-xs mt-1">Create your first organisation to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* {organizations.length === 1 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              One organisation per account. Contact support for more.
            </div>
          )} */}
          {organizations.map((org) => (
            <div
              key={org.id}
              className="flex items-center gap-4 p-4 border border-border/60 rounded-xl hover:border-primary/30 hover:bg-muted/20 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{org.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                  {org.role} · Created{" "}
                  {new Date(org.created_at).toLocaleDateString("en-AU", {
                    year: "numeric", month: "short", day: "numeric",
                  })}
                </p>
                {org.description && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">{org.description}</p>
                )}
              </div>
              <button
                onClick={() => onManage(org)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 hover:text-foreground transition-colors"
              >
                Manage <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Profile Settings Component ───────────────────────────────────────────────

const ProfileSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [formData, setFormData] = useState<{ full_name: string; phone: string }>({
    full_name: user?.user_metadata?.full_name || "",
    phone: user?.phone || "",
  });
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult>({
    isValid: true,
    formatted: "",
  });

  const loadProfileData = async () => {
    if (!user?.id) return;
    try {
      const { supabase } = await import("../utils/supabaseClient");
      const { data, error } = await supabase
        .from("base_profiles")
        .select("full_name, phone")
        .eq("id", user.id)
        .single();
      if (!error && data) {
        setFormData({ full_name: data.full_name || "", phone: data.phone || "" });
      } else {
        setFormData({ full_name: user.user_metadata?.full_name || "", phone: user.phone || "" });
      }
    } catch {
      setFormData({ full_name: user.user_metadata?.full_name || "", phone: user.phone || "" });
    }
  };

  useEffect(() => { if (user?.id) loadProfileData(); }, [user?.id]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      if (formData.phone && !phoneValidation.isValid) {
        toast({ title: "Validation Error", description: "Please enter a valid phone number in international format.", variant: "destructive" });
        return;
      }
      const { supabase } = await import("../utils/supabaseClient");
      const { error: profileError } = await supabase.from("base_profiles").upsert({
        id: user?.id,
        full_name: formData.full_name,
        phone: formData.phone || null,
        updated_at: new Date().toISOString(),
      });
      if (profileError) {
        if (profileError.message?.includes("Invalid phone number format") || profileError.message?.includes("E.164")) {
          toast({ title: "Phone Number Error", description: "Phone number must be in international format (e.g., +1234567890).", variant: "destructive" });
          return;
        }
        throw profileError;
      }
      const { error: authError } = await supabase.auth.updateUser({ data: { full_name: formData.full_name } });
      if (authError) console.warn("Auth metadata update failed:", authError);
      await loadProfileData();
      toast({ title: "Profile updated", description: "Your profile has been saved." });
    } catch {
      toast({ title: "Update failed", description: "Failed to update profile. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      setPasswordLoading(true);
      const { supabase } = await import("../utils/supabaseClient");
      const { error } = await supabase.auth.resetPasswordForEmail(user?.email || "", {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });
      if (error) throw error;
      toast({ title: "Password reset email sent", description: "Check your inbox for a secure link to set your new password." });
    } catch (error) {
      toast({ title: "Failed to send reset email", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) { toast({ title: "Validation error", description: "Please enter a new email address.", variant: "destructive" }); return; }
    if (newEmail.trim() === user?.email) { toast({ title: "Validation error", description: "New email must be different from your current email.", variant: "destructive" }); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) { toast({ title: "Validation error", description: "Please enter a valid email address.", variant: "destructive" }); return; }
    try {
      setEmailLoading(true);
      const { supabase } = await import("../utils/supabaseClient");
      const { error: authError } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (authError) throw authError;
      const { data: { session } } = await supabase.auth.getSession();
      await fetch("/api/user/email", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": user?.id || "",
          "x-api-key": import.meta.env.VITE_API_KEY || "scopingai",
          Authorization: session?.access_token ? `Bearer ${session.access_token}` : "",
        },
        body: JSON.stringify({ email: newEmail.trim() }),
      });
      toast({ title: "Confirmation email sent", description: `A confirmation link has been sent to ${newEmail.trim()}.` });
      setShowEmailForm(false);
      setNewEmail("");
    } catch (error) {
      toast({ title: "Email change failed", description: error instanceof Error ? error.message : "Failed to initiate email change.", variant: "destructive" });
    } finally {
      setEmailLoading(false);
    }
  };

  const initials = getInitials(formData.full_name || user?.user_metadata?.full_name, user?.email);

  return (
    <div className="space-y-5">
      {/* Avatar row */}
      <div className={`${CARD} p-6 flex items-center gap-5`}>
        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl flex-shrink-0">
          {initials}
        </div>
        <div>
          <p className="font-semibold text-base">{formData.full_name || "Your Name"}</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      {/* Personal info */}
      <div className={`${CARD} p-6`}>
        <h3 className="font-semibold mb-5 flex items-center gap-2">
          <User className="w-4 h-4 text-primary" /> Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium mb-1.5">Full Name</label>
            <input type="text" className={INPUT} placeholder="Enter your full name" value={formData.full_name} onChange={(e) => handleInputChange("full_name", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <input type="email" className={`${INPUT} bg-muted/50 cursor-not-allowed`} value={user?.email || ""} disabled />
            <p className="text-xs text-muted-foreground mt-1">Use "Change Email" below to update.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1.5">Phone</label>
            <PhoneInput
              value={formData.phone}
              onChange={(formatted, validation) => { setPhoneValidation(validation); handleInputChange("phone", formatted); }}
              placeholder="+61 412 345 678"
              className="w-full"
            />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={handleSaveProfile} disabled={loading} className={BTN_PRIMARY}>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Password & Security */}
      <div className={`${CARD} p-6`}>
        <h3 className="font-semibold mb-5 flex items-center gap-2">
          <Key className="w-4 h-4 text-primary" /> Password & Security
        </h3>

        <div className="flex items-center justify-between py-4 border-b border-border">
          <div>
            <p className="text-sm font-medium">Password</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              A reset link will be emailed to <span className="font-medium text-foreground">{user?.email}</span>
            </p>
          </div>
          <button onClick={handleUpdatePassword} disabled={passwordLoading} className={BTN_OUTLINE}>
            {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
            {passwordLoading ? "Sending…" : "Change Password"}
          </button>
        </div>

        <div className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium">Email Address</p>
              <p className="text-xs text-muted-foreground mt-0.5">Current: <span className="font-medium text-foreground">{user?.email}</span></p>
            </div>
            {!showEmailForm && (
              <button onClick={() => setShowEmailForm(true)} className={BTN_OUTLINE}>
                <Mail className="w-4 h-4" /> Change Email
              </button>
            )}
          </div>

          {showEmailForm && (
            <div className="space-y-3 p-4 rounded-xl bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground">A confirmation link will be sent to your new email. Click it to complete the change.</p>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                className={INPUT}
                disabled={emailLoading}
              />
              <div className="flex gap-2">
                <button onClick={() => { setShowEmailForm(false); setNewEmail(""); }} disabled={emailLoading} className={`flex-1 ${BTN_OUTLINE}`}>Cancel</button>
                <button onClick={handleChangeEmail} disabled={emailLoading || !newEmail.trim()} className={`flex-1 ${BTN_PRIMARY} justify-center`}>
                  {emailLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {emailLoading ? "Sending…" : "Send Confirmation"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Signature */}
      <EmailSignatureSection />

      {/* Danger Zone */}
      <DeleteAccountSection />
    </div>
  );
};

// ─── Delete Account Section ───────────────────────────────────────────────────

const DeleteAccountSection: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    try {
      const { supabase } = await import("../utils/supabaseClient");
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/user/account", {
        method: "DELETE",
        headers: {
          "x-user-id": user?.id || "",
          "x-api-key": import.meta.env.VITE_API_KEY || "scopingai",
          Authorization: session?.access_token ? `Bearer ${session.access_token}` : "",
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Deletion failed");
      }
      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (err: unknown) {
      toast({ title: "Failed to delete account", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
      setDeleting(false);
    }
  };

  return (
    <div className={`${CARD} border-destructive/20 p-6`}>
      <h3 className="font-semibold mb-1 flex items-center gap-2 text-destructive">
        <Trash2 className="w-4 h-4" /> Danger Zone
      </h3>
      <p className="text-xs text-muted-foreground mb-5">
        Permanently delete your account. Your personal data and org memberships will be removed.
        Tenders, proposals, and organisation data are not affected.
      </p>
      {!showConfirm ? (
        <button onClick={() => setShowConfirm(true)} className="px-5 py-2.5 border border-destructive text-destructive text-sm font-medium rounded-lg hover:bg-destructive/5 transition-colors">
          Delete My Account
        </button>
      ) : (
        <div className="space-y-4 p-4 rounded-xl bg-destructive/5 border border-destructive/20">
          <p className="text-sm font-medium text-destructive">
            This is irreversible. Type <span className="font-mono font-bold">DELETE</span> to confirm.
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className={`${INPUT} border-destructive/30`}
          />
          <div className="flex gap-2">
            <button onClick={() => { setShowConfirm(false); setConfirmText(""); }} className={`flex-1 ${BTN_OUTLINE}`}>Cancel</button>
            <button
              onClick={handleDelete}
              disabled={confirmText !== "DELETE" || deleting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-destructive text-white rounded-lg text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
              {deleting ? "Deleting…" : "Permanently Delete Account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Email Signature Section ──────────────────────────────────────────────────

const EmailSignatureSection: React.FC = () => {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [importingSignature, setImportingSignature] = useState<string | null>(null);
  const [sigImportResult, setSigImportResult] = useState<Record<string, "ok" | "empty" | "error">>({});

  useEffect(() => {
    emailApiService.getMyAccounts()
      .then(setAccounts)
      .catch((err: unknown) => console.error("Failed to load email accounts:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleImportSignature = async (accountId: string) => {
    try {
      setImportingSignature(accountId);
      const result = await emailApiService.getGmailSignature(accountId);
      if (result.hasSignature) {
        setSigImportResult((p) => ({ ...p, [accountId]: "ok" }));
        setAccounts((p) => p.map((a) => a.id === accountId ? { ...a, email_signature: result.signature } : a));
        toast({ title: "Signature imported", description: "Your Gmail signature has been saved." });
      } else {
        setSigImportResult((p) => ({ ...p, [accountId]: "empty" }));
        toast({ title: "No signature found", description: "No email signature was found in your Gmail settings.", variant: "destructive" });
      }
    } catch (err: unknown) {
      setSigImportResult((p) => ({ ...p, [accountId]: "error" }));
      toast({ title: "Import failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setImportingSignature(null);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const { url } = await emailApiService.connectAccount();
      window.location.href = url;
    } catch (err: unknown) {
      toast({ title: "Failed to connect Gmail", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    }
  };

  return (
    <div className={`${CARD} p-6`}>
      <h3 className="font-semibold mb-2 flex items-center gap-2">
        <Mail className="w-4 h-4 text-primary" /> Email Signature
      </h3>
      <p className="text-xs text-muted-foreground mb-5">
        Import your Gmail signature to append automatically when sending tender emails.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading accounts…</span>
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-border rounded-xl">
          <Mail className="w-8 h-8 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground mb-3">No Gmail account connected.</p>
          <button onClick={handleConnectGmail} className={BTN_PRIMARY}>
            <Mail className="w-4 h-4" /> Connect Gmail
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {accounts.map((account) => (
            <div key={account.id} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-1.5 rounded-full ${account.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{account.email}</p>
                    {!account.is_active ? (
                      <p className="text-xs text-red-500">Disconnected — reconnect to import signature</p>
                    ) : account.email_signature ? (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCheck className="w-3 h-3" /> Signature saved
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">No signature imported yet</p>
                    )}
                  </div>
                </div>
                {account.is_active ? (
                  <button
                    onClick={() => handleImportSignature(account.id)}
                    disabled={importingSignature === account.id}
                    className={BTN_PRIMARY}
                  >
                    {importingSignature === account.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : sigImportResult[account.id] === "ok" ? (
                      <CheckCheck className="w-4 h-4" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {sigImportResult[account.id] === "ok" ? "Imported!" : account.email_signature ? "Re-import" : "Import from Gmail"}
                  </button>
                ) : (
                  <button onClick={handleConnectGmail} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
                    <RefreshCw className="w-4 h-4" /> Reconnect
                  </button>
                )}
              </div>
              {account.is_active && account.email_signature && (
                <div className="rounded-xl bg-muted/40 border border-border p-4">
                  <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">Signature Preview</p>
                  <div className="bg-white rounded-lg p-3 text-sm" dangerouslySetInnerHTML={{ __html: account.email_signature }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Settings;
